interface Profile {
  id: string;
  first_name?: string;
  age: number;
  gender: string;
  height_feet?: number;
  height_inches?: number;
  importance_of_islam: number;
  sect: string;
  location: string;
  ethnicity: string;
  education_level: string;
  personality_type: number;
  financial_status: number;
  masjid_attendance: string;
  willing_to_relocate: boolean;
  wears_hijab?: boolean;
  wears_hijab_level?: string;
  seeking_age_min: number;
  seeking_age_max: number;
  seeking_height_min_feet?: number;
  seeking_height_min_inches?: number;
  seeking_height_max_feet?: number;
  seeking_height_max_inches?: number;
  seeking_islam_importance_min?: number;
  seeking_sect?: string[];
  seeking_location_preference?: string;
  seeking_ethnicity?: string[];
  seeking_education?: string[];
  seeking_personality_min?: number;
  seeking_personality_max?: number;
  seeking_financial_min?: number;
  seeking_masjid_attendance_min?: string;
  seeking_hijab_preference?: string;
  seeking_beard_preference?: string;
  has_beard?: string;
  seeking_preferences_weights?: any;
  marital_status: string;
  has_children: boolean;
  willing_to_marry_convert: boolean;
  willing_to_marry_divorced: boolean;
  willing_to_marry_with_children: boolean;
  seeking_marital_status?: string[];
  seeking_convert_ok?: boolean;
  seeking_divorced_ok?: boolean;
  seeking_children_ok?: boolean;
}

import {
  IMPORTANCE_LEVEL,
  IMPORTANCE_MULTIPLIERS,
  type ImportanceLevel,
  MASJID_ATTENDANCE,
  HIJAB_LEVEL,
  BEARD_LEVEL,
  HIJAB_PREFERENCE,
  BEARD_PREFERENCE,
  ordinalIndex,
  isNoPreference,
  educationOrdinal,
} from "@/lib/enums";

export type { ImportanceLevel };

export interface WeightedCompatibilityDetail {
  match: boolean | "close" | "partial" | "open" | "not_considered";
  points: number;
  maxPoints?: number;
  note?: string;
  issue?: string;
  dealBreakerFailed?: boolean;
  importance?: {
    profile1: ImportanceLevel;
    profile2: ImportanceLevel;
  };
}

export interface WeightedCompatibilityResult {
  score: number;
  totalPossiblePoints: number;
  earnedPoints: number;
  details: {
    age?: WeightedCompatibilityDetail;
    height?: WeightedCompatibilityDetail;
    religiosity?: WeightedCompatibilityDetail;
    sect?: WeightedCompatibilityDetail;
    location?: WeightedCompatibilityDetail;
    ethnicity?: WeightedCompatibilityDetail;
    education?: WeightedCompatibilityDetail;
    personality?: WeightedCompatibilityDetail;
    financial?: WeightedCompatibilityDetail;
    hijab?: WeightedCompatibilityDetail;
    beard?: WeightedCompatibilityDetail;
    masjidAttendance?: WeightedCompatibilityDetail;
    maritalStatus?: WeightedCompatibilityDetail;
    children?: WeightedCompatibilityDetail;
  };
  dealBreakerFailed: boolean;
  dealBreakers: string[];
  matchType: "perfect" | "close" | "soft" | "low";
  weightedFactors: {
    mostImportantToProfile1: string[];
    mostImportantToProfile2: string[];
    mutualPriorities: string[];
  };
}

const DEFAULT_WEIGHTS = {
  age: "important",
  height: "somewhat_important",
  ethnicity: "flexible",
  religiosity: "very_important",
  sect: "important",
  education: "somewhat_important",
  location: "important",
  marital_status: "somewhat_important",
  children: "flexible",
  personality: "somewhat_important",
  financial: "somewhat_important",
  masjid_attendance: "important",
  // Note: hijab and beard are NOT in defaults - they're gender-specific
  // Males should explicitly set hijab importance, females should set beard importance
  // If missing, they default to 0 (dont_care) in the calculation
};

export function calculateWeightedCompatibility(
  profile1: Profile,
  profile2: Profile
): WeightedCompatibilityResult {
  let totalPossiblePoints = 0;
  let earnedPoints = 0;
  const details: WeightedCompatibilityResult["details"] = {};
  const dealBreakers: string[] = [];
  let dealBreakerFailed = false;

  const weights1 = { ...DEFAULT_WEIGHTS, ...profile1.seeking_preferences_weights };
  const weights2 = { ...DEFAULT_WEIGHTS, ...profile2.seeking_preferences_weights };

  // Get names for more readable messages
  const name1 = profile1.first_name || "Profile 1";
  const name2 = profile2.first_name || "Profile 2";

  // AGE COMPATIBILITY
  const ageImportance1 = IMPORTANCE_MULTIPLIERS[weights1.age as ImportanceLevel] || 1.5;
  const ageImportance2 = IMPORTANCE_MULTIPLIERS[weights2.age as ImportanceLevel] || 1.5;
  const ageBasePoints = 20;

  // Check deal-breaker
  if (weights1.age === "deal_breaker" || weights2.age === "deal_breaker") {
    const ageInRange1 = profile1.age >= profile2.seeking_age_min && profile1.age <= profile2.seeking_age_max;
    const ageInRange2 = profile2.age >= profile1.seeking_age_min && profile2.age <= profile1.seeking_age_max;

    if (!ageInRange1 || !ageInRange2) {
      dealBreakerFailed = true;
      dealBreakers.push("Age is a deal-breaker and doesn't match");
      details.age = {
        match: false,
        points: 0,
        dealBreakerFailed: true,
        issue: "Age deal-breaker not met",
      };
      return {
        score: 0,
        totalPossiblePoints: 0,
        earnedPoints: 0,
        details,
        dealBreakerFailed: true,
        dealBreakers,
        matchType: "low",
        weightedFactors: {
          mostImportantToProfile1: [],
          mostImportantToProfile2: [],
          mutualPriorities: [],
        },
      };
    }
  }

  const ageWeight = (ageImportance1 + ageImportance2) / 2;
  if (ageWeight > 0) {
    totalPossiblePoints += ageBasePoints * ageWeight;

    const ageInRange1 = profile1.age >= profile2.seeking_age_min && profile1.age <= profile2.seeking_age_max;
    const ageInRange2 = profile2.age >= profile1.seeking_age_min && profile2.age <= profile1.seeking_age_max;

    // Build standardized format
    const p1Label = getImportanceLabel(weights1.age as ImportanceLevel);
    const p2Label = getImportanceLabel(weights2.age as ImportanceLevel);
    const emoji1 = ageInRange1 ? "✓" : "✗";
    const emoji2 = ageInRange2 ? "✓" : "✗";

    const noteLines = [
      `${name1} is ${profile1.age} (${name2} seeking: ${profile2.seeking_age_min}-${profile2.seeking_age_max}) ${emoji1}`,
      `  ${name2}'s priority: ${p2Label}`,
      `${name2} is ${profile2.age} (${name1} seeking: ${profile1.seeking_age_min}-${profile1.seeking_age_max}) ${emoji2}`,
      `  ${name1}'s priority: ${p1Label}`
    ];
    const formattedNote = noteLines.join('\n');

    if (ageInRange1 && ageInRange2) {
      earnedPoints += ageBasePoints * ageWeight;
      details.age = {
        match: true,
        points: ageBasePoints * ageWeight,
        maxPoints: ageBasePoints * ageWeight,
        note: formattedNote,
        importance: { profile1: weights1.age as ImportanceLevel, profile2: weights2.age as ImportanceLevel },
      };
    } else {
      const age1Diff = Math.min(
        Math.abs(profile1.age - profile2.seeking_age_min),
        Math.abs(profile1.age - profile2.seeking_age_max)
      );
      const age2Diff = Math.min(
        Math.abs(profile2.age - profile1.seeking_age_min),
        Math.abs(profile2.age - profile1.seeking_age_max)
      );
      const maxDiff = Math.max(age1Diff, age2Diff);

      if (maxDiff <= 2) {
        const points = (ageBasePoints * 0.75) * ageWeight;
        earnedPoints += points;
        details.age = {
          match: "close",
          points,
          maxPoints: ageBasePoints * ageWeight,
          note: formattedNote,
          importance: { profile1: weights1.age as ImportanceLevel, profile2: weights2.age as ImportanceLevel },
        };
      } else if (maxDiff <= 5) {
        const points = (ageBasePoints * 0.5) * ageWeight;
        earnedPoints += points;
        details.age = {
          match: "partial",
          points,
          maxPoints: ageBasePoints * ageWeight,
          note: formattedNote,
          importance: { profile1: weights1.age as ImportanceLevel, profile2: weights2.age as ImportanceLevel },
        };
      } else {
        details.age = {
          match: false,
          points: 0,
          maxPoints: ageBasePoints * ageWeight,
          note: formattedNote,
          importance: { profile1: weights1.age as ImportanceLevel, profile2: weights2.age as ImportanceLevel },
        };
      }
    }
  } else {
    details.age = { match: "not_considered", points: 0, note: "Age not a priority for both" };
  }

  // HEIGHT COMPATIBILITY
  const heightImportance1 = IMPORTANCE_MULTIPLIERS[weights1.height as ImportanceLevel] || 1.0;
  const heightImportance2 = IMPORTANCE_MULTIPLIERS[weights2.height as ImportanceLevel] || 1.0;
  const heightBasePoints = 10;
  const heightWeight = (heightImportance1 + heightImportance2) / 2;

  // Use explicit null checks; 0 inches is a valid value (e.g. 5'0", 6'0") and
  // must not short-circuit the `feet && inches` truthy check that bug-rounded
  // those bounds to 0".
  if (heightWeight > 0 && profile1.height_feet != null && profile2.height_feet != null) {
    totalPossiblePoints += heightBasePoints * heightWeight;

    const height1Inches = profile1.height_feet * 12 + (profile1.height_inches ?? 0);
    const height2Inches = profile2.height_feet * 12 + (profile2.height_inches ?? 0);

    const minHeight1 =
      profile1.seeking_height_min_feet != null
        ? profile1.seeking_height_min_feet * 12 + (profile1.seeking_height_min_inches ?? 0)
        : 0;
    const maxHeight1 =
      profile1.seeking_height_max_feet != null
        ? profile1.seeking_height_max_feet * 12 + (profile1.seeking_height_max_inches ?? 0)
        : 96;
    const minHeight2 =
      profile2.seeking_height_min_feet != null
        ? profile2.seeking_height_min_feet * 12 + (profile2.seeking_height_min_inches ?? 0)
        : 0;
    const maxHeight2 =
      profile2.seeking_height_max_feet != null
        ? profile2.seeking_height_max_feet * 12 + (profile2.seeking_height_max_inches ?? 0)
        : 96;

    const heightMatch1 = height2Inches >= minHeight1 && height2Inches <= maxHeight1;
    const heightMatch2 = height1Inches >= minHeight2 && height1Inches <= maxHeight2;

    // Build standardized format
    const p1Label = getImportanceLabel(weights1.height as ImportanceLevel);
    const p2Label = getImportanceLabel(weights2.height as ImportanceLevel);
    const h1Str = `${profile1.height_feet}'${profile1.height_inches}"`;
    const h2Str = `${profile2.height_feet}'${profile2.height_inches}"`;
    const minHeight1Str = `${Math.floor(minHeight1/12)}'${minHeight1%12}"`;
    const maxHeight1Str = `${Math.floor(maxHeight1/12)}'${maxHeight1%12}"`;
    const minHeight2Str = `${Math.floor(minHeight2/12)}'${minHeight2%12}"`;
    const maxHeight2Str = `${Math.floor(maxHeight2/12)}'${maxHeight2%12}"`;
    const emoji1 = heightMatch1 ? "✓" : "✗";
    const emoji2 = heightMatch2 ? "✓" : "✗";

    const noteLines = [
      `${name1} is ${h1Str} (${name2} seeking: ${minHeight2Str}-${maxHeight2Str}) ${emoji1}`,
      `  ${name2}'s priority: ${p2Label}`,
      `${name2} is ${h2Str} (${name1} seeking: ${minHeight1Str}-${maxHeight1Str}) ${emoji2}`,
      `  ${name1}'s priority: ${p1Label}`
    ];
    const formattedNote = noteLines.join('\n');

    if (heightMatch1 && heightMatch2) {
      earnedPoints += heightBasePoints * heightWeight;
      details.height = {
        match: true,
        points: heightBasePoints * heightWeight,
        maxPoints: heightBasePoints * heightWeight,
        note: formattedNote,
        importance: { profile1: weights1.height as ImportanceLevel, profile2: weights2.height as ImportanceLevel },
      };
    } else if (heightMatch1 || heightMatch2) {
      const points = (heightBasePoints * 0.5) * heightWeight;
      earnedPoints += points;
      details.height = {
        match: "partial",
        points,
        maxPoints: heightBasePoints * heightWeight,
        note: formattedNote,
        importance: { profile1: weights1.height as ImportanceLevel, profile2: weights2.height as ImportanceLevel },
      };
    } else {
      details.height = {
        match: false,
        points: 0,
        maxPoints: heightBasePoints * heightWeight,
        note: formattedNote,
        importance: { profile1: weights1.height as ImportanceLevel, profile2: weights2.height as ImportanceLevel },
      };
    }
  } else if (heightWeight > 0) {
    details.height = {
      match: "not_considered",
      points: 0,
      note: "Height data incomplete",
      importance: { profile1: weights1.height as ImportanceLevel, profile2: weights2.height as ImportanceLevel },
    };
  } else {
    details.height = { match: "not_considered", points: 0, note: "Height not a priority" };
  }

  // RELIGIOSITY ALIGNMENT
  const religiosityImportance1 = IMPORTANCE_MULTIPLIERS[weights1.religiosity as ImportanceLevel] || 2.0;
  const religiosityImportance2 = IMPORTANCE_MULTIPLIERS[weights2.religiosity as ImportanceLevel] || 2.0;
  const religiosityBasePoints = 20;
  const religiosityWeight = (religiosityImportance1 + religiosityImportance2) / 2;

  if (religiosityWeight > 0) {
    totalPossiblePoints += religiosityBasePoints * religiosityWeight;

    const religiosityDiff = Math.abs(profile1.importance_of_islam - profile2.importance_of_islam);
    const minImportance1 = profile1.seeking_islam_importance_min || 1;
    const minImportance2 = profile2.seeking_islam_importance_min || 1;

    const meetsMin1 = profile2.importance_of_islam >= minImportance1;
    const meetsMin2 = profile1.importance_of_islam >= minImportance2;

    // Build standardized format
    const p1Label = getImportanceLabel(weights1.religiosity as ImportanceLevel);
    const p2Label = getImportanceLabel(weights2.religiosity as ImportanceLevel);
    const emoji1 = meetsMin1 ? "✓" : "✗";
    const emoji2 = meetsMin2 ? "✓" : "✗";

    const noteLines = [
      `${name1} has ${profile1.importance_of_islam}/10 importance of Islam (${name2} seeking: min ${minImportance2}) ${emoji1}`,
      `  ${name2}'s priority: ${p2Label}`,
      `${name2} has ${profile2.importance_of_islam}/10 importance of Islam (${name1} seeking: min ${minImportance1}) ${emoji2}`,
      `  ${name1}'s priority: ${p1Label}`
    ];
    const formattedNote = noteLines.join('\n');

    if (meetsMin1 && meetsMin2 && religiosityDiff <= 1) {
      earnedPoints += religiosityBasePoints * religiosityWeight;
      details.religiosity = {
        match: true,
        points: religiosityBasePoints * religiosityWeight,
        maxPoints: religiosityBasePoints * religiosityWeight,
        note: formattedNote,
        importance: {
          profile1: weights1.religiosity as ImportanceLevel,
          profile2: weights2.religiosity as ImportanceLevel,
        },
      };
    } else if (meetsMin1 && meetsMin2 && religiosityDiff <= 2) {
      const points = (religiosityBasePoints * 0.75) * religiosityWeight;
      earnedPoints += points;
      details.religiosity = {
        match: "close",
        points,
        maxPoints: religiosityBasePoints * religiosityWeight,
        note: formattedNote,
        importance: { profile1: weights1.religiosity as ImportanceLevel, profile2: weights2.religiosity as ImportanceLevel },
      };
    } else if (meetsMin1 && meetsMin2 && religiosityDiff <= 3) {
      const points = (religiosityBasePoints * 0.5) * religiosityWeight;
      earnedPoints += points;
      details.religiosity = {
        match: "partial",
        points,
        maxPoints: religiosityBasePoints * religiosityWeight,
        note: formattedNote,
        importance: { profile1: weights1.religiosity as ImportanceLevel, profile2: weights2.religiosity as ImportanceLevel },
      };
    } else {
      details.religiosity = {
        match: false,
        points: 0,
        maxPoints: religiosityBasePoints * religiosityWeight,
        note: formattedNote,
        importance: { profile1: weights1.religiosity as ImportanceLevel, profile2: weights2.religiosity as ImportanceLevel },
      };
    }
  } else {
    details.religiosity = { match: "not_considered", points: 0, note: "Religiosity not a priority for both" };
  }

  // SECT COMPATIBILITY (Pass/Fail only, no points)
  const sectOptions1 = profile1.seeking_sect || [profile1.sect, "No preference"];
  const sectOptions2 = profile2.seeking_sect || [profile2.sect, "No preference"];

  const sectOptions2HasNoPref = sectOptions2.some((s) => isNoPreference(s));
  const sectOptions1HasNoPref = sectOptions1.some((s) => isNoPreference(s));
  const profile1MatchesSect = sectOptions2.includes(profile1.sect) || sectOptions2HasNoPref || isNoPreference(profile1.sect);
  const profile2MatchesSect = sectOptions1.includes(profile2.sect) || sectOptions1HasNoPref || isNoPreference(profile2.sect);
  const eitherJustMuslim = profile1.sect === "Just Muslim" || profile2.sect === "Just Muslim";

  const sectP1Label = getImportanceLabel(weights1.sect as ImportanceLevel);
  const sectP2Label = getImportanceLabel(weights2.sect as ImportanceLevel);
  const sectEmoji1 = profile1MatchesSect || eitherJustMuslim ? "✓" : "✗";
  const sectEmoji2 = profile2MatchesSect || eitherJustMuslim ? "✓" : "✗";

  const sectNoteLines = [
    `${name1} is ${profile1.sect} (${name2} seeking: ${sectOptions2.join(", ")}) ${sectEmoji1}`,
    `  ${name2}'s priority: ${sectP2Label}`,
    `${name2} is ${profile2.sect} (${name1} seeking: ${sectOptions1.join(", ")}) ${sectEmoji2}`,
    `  ${name1}'s priority: ${sectP1Label}`
  ];
  const formattedSectNote = sectNoteLines.join('\n');

  if (!profile1MatchesSect || !profile2MatchesSect) {
    // Check if it's a deal-breaker
    if ((weights1.sect === "deal_breaker" || weights2.sect === "deal_breaker") && !eitherJustMuslim) {
      dealBreakerFailed = true;
      dealBreakers.push(`Sect mismatch: ${formattedSectNote}`);
      details.sect = {
        match: false,
        points: 0,
        note: formattedSectNote,
        dealBreakerFailed: true,
        importance: { profile1: weights1.sect as ImportanceLevel, profile2: weights2.sect as ImportanceLevel },
      };
    } else {
      // Not a match but not a deal-breaker - pass/fail only, no points
      details.sect = {
        match: false,
        points: 0,
        note: formattedSectNote,
        importance: { profile1: weights1.sect as ImportanceLevel, profile2: weights2.sect as ImportanceLevel },
      };
    }
  } else {
    // Match - pass/fail only, no points added
    details.sect = {
      match: true,
      points: 0,
      note: formattedSectNote,
      importance: { profile1: weights1.sect as ImportanceLevel, profile2: weights2.sect as ImportanceLevel },
    };
  }

  // LOCATION COMPATIBILITY
  const locationImportance1 = IMPORTANCE_MULTIPLIERS[weights1.location as ImportanceLevel] || 1.5;
  const locationImportance2 = IMPORTANCE_MULTIPLIERS[weights2.location as ImportanceLevel] || 1.5;
  const locationBasePoints = 10;
  const locationWeight = (locationImportance1 + locationImportance2) / 2;

  if (locationWeight > 0) {
    totalPossiblePoints += locationBasePoints * locationWeight;

    const sameLocation = profile1.location.toLowerCase() === profile2.location.toLowerCase();
    const p1Label = getImportanceLabel(weights1.location as ImportanceLevel);
    const p2Label = getImportanceLabel(weights2.location as ImportanceLevel);
    const emoji1 = sameLocation || profile1.willing_to_relocate ? "✓" : "✗";
    const emoji2 = sameLocation || profile2.willing_to_relocate ? "✓" : "✗";

    const noteLines = [
      `${name1} is in ${profile1.location}, willing to relocate: ${profile1.willing_to_relocate ? "Yes" : "No"} (${name2} in ${profile2.location}) ${emoji1}`,
      `  ${name2}'s priority: ${p2Label}`,
      `${name2} is in ${profile2.location}, willing to relocate: ${profile2.willing_to_relocate ? "Yes" : "No"} (${name1} in ${profile1.location}) ${emoji2}`,
      `  ${name1}'s priority: ${p1Label}`
    ];
    const formattedNote = noteLines.join('\n');

    if (sameLocation) {
      earnedPoints += locationBasePoints * locationWeight;
      details.location = {
        match: true,
        points: locationBasePoints * locationWeight,
        maxPoints: locationBasePoints * locationWeight,
        note: formattedNote,
        importance: { profile1: weights1.location as ImportanceLevel, profile2: weights2.location as ImportanceLevel },
      };
    } else if (profile1.willing_to_relocate || profile2.willing_to_relocate) {
      const points = (locationBasePoints * 0.5) * locationWeight;
      earnedPoints += points;
      details.location = {
        match: "open",
        points,
        maxPoints: locationBasePoints * locationWeight,
        note: formattedNote,
        importance: { profile1: weights1.location as ImportanceLevel, profile2: weights2.location as ImportanceLevel },
      };
    } else {
      details.location = {
        match: false,
        points: 0,
        maxPoints: locationBasePoints * locationWeight,
        note: formattedNote,
        importance: { profile1: weights1.location as ImportanceLevel, profile2: weights2.location as ImportanceLevel },
      };
    }
  } else {
    details.location = { match: "not_considered", points: 0, note: "Location not a priority for both" };
  }

  // ETHNICITY COMPATIBILITY
  const ethnicityImportance1 = IMPORTANCE_MULTIPLIERS[weights1.ethnicity as ImportanceLevel] || 0.5;
  const ethnicityImportance2 = IMPORTANCE_MULTIPLIERS[weights2.ethnicity as ImportanceLevel] || 0.5;
  const ethnicityBasePoints = 10;
  const ethnicityWeight = (ethnicityImportance1 + ethnicityImportance2) / 2;

  if (ethnicityWeight > 0) {
    totalPossiblePoints += ethnicityBasePoints * ethnicityWeight;

    const ethnicityOptions1 = profile1.seeking_ethnicity || [profile1.ethnicity];
    const ethnicityOptions2 = profile2.seeking_ethnicity || [profile2.ethnicity];

    const profile1MatchesProfile2Seeking = ethnicityOptions2.includes(profile1.ethnicity);
    const profile2MatchesProfile1Seeking = ethnicityOptions1.includes(profile2.ethnicity);

    const p1Label = getImportanceLabel(weights1.ethnicity as ImportanceLevel);
    const p2Label = getImportanceLabel(weights2.ethnicity as ImportanceLevel);
    const emoji1 = profile1MatchesProfile2Seeking ? "✓" : "✗";
    const emoji2 = profile2MatchesProfile1Seeking ? "✓" : "✗";

    const noteLines = [
      `${name1} is ${profile1.ethnicity} (${name2} seeking: ${ethnicityOptions2.join(", ")}) ${emoji1}`,
      `  ${name2}'s priority: ${p2Label}`,
      `${name2} is ${profile2.ethnicity} (${name1} seeking: ${ethnicityOptions1.join(", ")}) ${emoji2}`,
      `  ${name1}'s priority: ${p1Label}`
    ];
    const formattedNote = noteLines.join('\n');

    if (profile1MatchesProfile2Seeking && profile2MatchesProfile1Seeking) {
      earnedPoints += ethnicityBasePoints * ethnicityWeight;
      details.ethnicity = {
        match: true,
        points: ethnicityBasePoints * ethnicityWeight,
        maxPoints: ethnicityBasePoints * ethnicityWeight,
        note: formattedNote,
        importance: { profile1: weights1.ethnicity as ImportanceLevel, profile2: weights2.ethnicity as ImportanceLevel },
      };
    } else if (ethnicityOptions1.some((e) => isNoPreference(e)) || ethnicityOptions2.some((e) => isNoPreference(e))) {
      const points = (ethnicityBasePoints * 0.8) * ethnicityWeight;
      earnedPoints += points;
      details.ethnicity = {
        match: "open",
        points,
        maxPoints: ethnicityBasePoints * ethnicityWeight,
        note: formattedNote,
        importance: { profile1: weights1.ethnicity as ImportanceLevel, profile2: weights2.ethnicity as ImportanceLevel },
      };
    } else if (profile1MatchesProfile2Seeking || profile2MatchesProfile1Seeking) {
      const points = (ethnicityBasePoints * 0.5) * ethnicityWeight;
      earnedPoints += points;
      details.ethnicity = {
        match: "partial",
        points,
        maxPoints: ethnicityBasePoints * ethnicityWeight,
        note: formattedNote,
        importance: { profile1: weights1.ethnicity as ImportanceLevel, profile2: weights2.ethnicity as ImportanceLevel },
      };
    } else {
      details.ethnicity = {
        match: false,
        points: 0,
        maxPoints: ethnicityBasePoints * ethnicityWeight,
        note: formattedNote,
        importance: { profile1: weights1.ethnicity as ImportanceLevel, profile2: weights2.ethnicity as ImportanceLevel },
      };
    }
  } else {
    details.ethnicity = { match: "not_considered", points: 0, note: "Ethnicity not a priority for both" };
  }

  // EDUCATION COMPATIBILITY
  const educationImportance1 = IMPORTANCE_MULTIPLIERS[weights1.education as ImportanceLevel] || 1.0;
  const educationImportance2 = IMPORTANCE_MULTIPLIERS[weights2.education as ImportanceLevel] || 1.0;
  const educationBasePoints = 5;
  const educationWeight = (educationImportance1 + educationImportance2) / 2;

  if (educationWeight > 0) {
    totalPossiblePoints += educationBasePoints * educationWeight;

    const educationOptions1 = profile1.seeking_education || [profile1.education_level];
    const educationOptions2 = profile2.seeking_education || [profile2.education_level];

    const candidateMeetsSeeker = (candidateLevel: string, seekerOptions: string[] | null | undefined): boolean => {
      if (!seekerOptions || seekerOptions.length === 0) return true;
      // "No preference" sentinel — any value is acceptable.
      const filtered = seekerOptions.filter((o) => o && !isNoPreference(o));
      if (filtered.length === 0) return true;
      // Try ordinal compare via SSoT. Unknown values return null and we
      // fall back to set membership so legacy/non-canonical labels still work.
      const ordinals = filtered.map((o) => educationOrdinal(o)).filter((n): n is number => typeof n === "number");
      if (ordinals.length === 0) return seekerOptions.includes(candidateLevel);
      const minRequired = Math.min(...ordinals);
      const candidateOrdinal = educationOrdinal(candidateLevel);
      if (candidateOrdinal == null) return seekerOptions.includes(candidateLevel);
      return candidateOrdinal >= minRequired;
    };

    const profile1MatchesEduc = candidateMeetsSeeker(profile1.education_level, educationOptions2);
    const profile2MatchesEduc = candidateMeetsSeeker(profile2.education_level, educationOptions1);

    const p1Label = getImportanceLabel(weights1.education as ImportanceLevel);
    const p2Label = getImportanceLabel(weights2.education as ImportanceLevel);
    const emoji1 = profile1MatchesEduc ? "✓" : "✗";
    const emoji2 = profile2MatchesEduc ? "✓" : "✗";

    const noteLines = [
      `${name1} has ${profile1.education_level} (${name2} seeking: ${educationOptions2.join(", ")}) ${emoji1}`,
      `  ${name2}'s priority: ${p2Label}`,
      `${name2} has ${profile2.education_level} (${name1} seeking: ${educationOptions1.join(", ")}) ${emoji2}`,
      `  ${name1}'s priority: ${p1Label}`
    ];
    const formattedNote = noteLines.join('\n');

    if (profile1MatchesEduc && profile2MatchesEduc) {
      earnedPoints += educationBasePoints * educationWeight;
      details.education = {
        match: true,
        points: educationBasePoints * educationWeight,
        maxPoints: educationBasePoints * educationWeight,
        note: formattedNote,
        importance: { profile1: weights1.education as ImportanceLevel, profile2: weights2.education as ImportanceLevel },
      };
    } else {
      const points = (educationBasePoints * 0.4) * educationWeight;
      earnedPoints += points;
      details.education = {
        match: "partial",
        points,
        maxPoints: educationBasePoints * educationWeight,
        note: formattedNote,
        importance: { profile1: weights1.education as ImportanceLevel, profile2: weights2.education as ImportanceLevel },
      };
    }
  } else {
    details.education = { match: "not_considered", points: 0, note: "Education not a priority for both" };
  }

  // PERSONALITY COMPATIBILITY
  const personalityImportance1 = IMPORTANCE_MULTIPLIERS[weights1.personality as ImportanceLevel] || 1.0;
  const personalityImportance2 = IMPORTANCE_MULTIPLIERS[weights2.personality as ImportanceLevel] || 1.0;
  const personalityBasePoints = 10;
  const personalityWeight = (personalityImportance1 + personalityImportance2) / 2;

  if (personalityWeight > 0) {
    totalPossiblePoints += personalityBasePoints * personalityWeight;

    const minPersonality1 = profile1.seeking_personality_min || 1;
    const maxPersonality1 = profile1.seeking_personality_max || 10;
    const minPersonality2 = profile2.seeking_personality_min || 1;
    const maxPersonality2 = profile2.seeking_personality_max || 10;

    const personalityMatch1 =
      profile2.personality_type >= minPersonality1 && profile2.personality_type <= maxPersonality1;
    const personalityMatch2 =
      profile1.personality_type >= minPersonality2 && profile1.personality_type <= maxPersonality2;

    const p1Label = getImportanceLabel(weights1.personality as ImportanceLevel);
    const p2Label = getImportanceLabel(weights2.personality as ImportanceLevel);
    const emoji1 = personalityMatch2 ? "✓" : "✗";
    const emoji2 = personalityMatch1 ? "✓" : "✗";

    const noteLines = [
      `${name1} has personality type ${profile1.personality_type} (${name2} seeking: ${minPersonality2}-${maxPersonality2}) ${emoji1}`,
      `  ${name2}'s priority: ${p2Label}`,
      `${name2} has personality type ${profile2.personality_type} (${name1} seeking: ${minPersonality1}-${maxPersonality1}) ${emoji2}`,
      `  ${name1}'s priority: ${p1Label}`
    ];
    const formattedNote = noteLines.join('\n');

    if (personalityMatch1 && personalityMatch2) {
      earnedPoints += personalityBasePoints * personalityWeight;
      details.personality = {
        match: true,
        points: personalityBasePoints * personalityWeight,
        maxPoints: personalityBasePoints * personalityWeight,
        note: formattedNote,
        importance: { profile1: weights1.personality as ImportanceLevel, profile2: weights2.personality as ImportanceLevel },
      };
    } else if (personalityMatch1 || personalityMatch2) {
      const points = (personalityBasePoints * 0.5) * personalityWeight;
      earnedPoints += points;
      details.personality = {
        match: "partial",
        points,
        maxPoints: personalityBasePoints * personalityWeight,
        note: formattedNote,
        importance: { profile1: weights1.personality as ImportanceLevel, profile2: weights2.personality as ImportanceLevel },
      };
    } else {
      details.personality = {
        match: false,
        points: 0,
        maxPoints: personalityBasePoints * personalityWeight,
        note: formattedNote,
        importance: { profile1: weights1.personality as ImportanceLevel, profile2: weights2.personality as ImportanceLevel },
      };
    }
  } else {
    details.personality = { match: "not_considered", points: 0, note: "Personality not a priority for both" };
  }

  // FINANCIAL STATUS COMPATIBILITY
  const financialImportance1 = IMPORTANCE_MULTIPLIERS[weights1.financial as ImportanceLevel] || 1.0;
  const financialImportance2 = IMPORTANCE_MULTIPLIERS[weights2.financial as ImportanceLevel] || 1.0;
  const financialBasePoints = 10;
  const financialWeight = (financialImportance1 + financialImportance2) / 2;

  if (financialWeight > 0) {
    totalPossiblePoints += financialBasePoints * financialWeight;

    const minFinancial1 = profile1.seeking_financial_min || 1;
    const minFinancial2 = profile2.seeking_financial_min || 1;

    const financialMatch1 = profile2.financial_status >= minFinancial1;
    const financialMatch2 = profile1.financial_status >= minFinancial2;

    const p1Label = getImportanceLabel(weights1.financial as ImportanceLevel);
    const p2Label = getImportanceLabel(weights2.financial as ImportanceLevel);
    const emoji1 = financialMatch2 ? "✓" : "✗";
    const emoji2 = financialMatch1 ? "✓" : "✗";

    const noteLines = [
      `${name1} has financial status ${profile1.financial_status}/10 (${name2} seeking: min ${minFinancial2}/10) ${emoji1}`,
      `  ${name2}'s priority: ${p2Label}`,
      `${name2} has financial status ${profile2.financial_status}/10 (${name1} seeking: min ${minFinancial1}/10) ${emoji2}`,
      `  ${name1}'s priority: ${p1Label}`
    ];
    const formattedNote = noteLines.join('\n');

    if (financialMatch1 && financialMatch2) {
      earnedPoints += financialBasePoints * financialWeight;
      details.financial = {
        match: true,
        points: financialBasePoints * financialWeight,
        maxPoints: financialBasePoints * financialWeight,
        note: formattedNote,
        importance: { profile1: weights1.financial as ImportanceLevel, profile2: weights2.financial as ImportanceLevel },
      };
    } else if (financialMatch1 || financialMatch2) {
      const points = (financialBasePoints * 0.5) * financialWeight;
      earnedPoints += points;
      details.financial = {
        match: "partial",
        points,
        maxPoints: financialBasePoints * financialWeight,
        note: formattedNote,
        importance: { profile1: weights1.financial as ImportanceLevel, profile2: weights2.financial as ImportanceLevel },
      };
    } else {
      details.financial = {
        match: false,
        points: 0,
        maxPoints: financialBasePoints * financialWeight,
        note: formattedNote,
        importance: { profile1: weights1.financial as ImportanceLevel, profile2: weights2.financial as ImportanceLevel },
      };
    }
  } else {
    details.financial = { match: "not_considered", points: 0, note: "Financial status not a priority for both" };
  }

  // Map a male's hijab preference text to {min, preferred} ordinal indices into
  // HIJAB_LEVEL. Returns null if there's no requirement (No preference / unknown).
  const getHijabRequirement = (
    pref: string | null | undefined,
  ): { min: number; preferred: number } | null => {
    if (pref == null || isNoPreference(pref)) return null;
    switch (pref) {
      case "Must wear":
        return { min: HIJAB_LEVEL.indexOf("Yes, usually"), preferred: HIJAB_LEVEL.indexOf("Yes, always") };
      case "Prefer yes":
        return { min: HIJAB_LEVEL.indexOf("Sometimes"), preferred: HIJAB_LEVEL.indexOf("Yes, usually") };
      case "Prefer no":
        return { min: 0, preferred: HIJAB_LEVEL.indexOf("No, but planning to") };
      default:
        console.warn(`[enums] Unknown hijab preference "${pref}". Treating as no preference.`);
        return null;
    }
  };

  // HIJAB COMPATIBILITY (male seeking female with hijab)
  // Only the MALE's importance weight matters here
  if (profile1.gender === "female" || profile2.gender === "female") {
    const femaleProfile = profile1.gender === "female" ? profile1 : profile2;
    const maleProfile = profile1.gender === "male" ? profile1 : profile2;
    const maleWeights = profile1.gender === "male" ? weights1 : weights2;
    const femaleName = femaleProfile.first_name || "Female";
    const maleName = maleProfile.first_name || "Male";

    const hijabImportance = maleWeights.hijab ? IMPORTANCE_MULTIPLIERS[maleWeights.hijab as ImportanceLevel] : 0.0;
    const hijabBasePoints = 15;

    // For display: only male's importance matters, female's is always "dont_care"
    const hijabImportanceDisplay = {
      profile1: profile1.gender === "male" ? (maleWeights.hijab as ImportanceLevel || "dont_care") : "dont_care",
      profile2: profile2.gender === "male" ? (maleWeights.hijab as ImportanceLevel || "dont_care") : "dont_care",
    };

    if (hijabImportance > 0) {
      const malePreference = maleProfile.seeking_hijab_preference;
      const femaleHijabLevel = femaleProfile.wears_hijab_level;

      const maleLabel = getImportanceLabel(maleWeights.hijab as ImportanceLevel);

      const requirement = getHijabRequirement(malePreference);
      const femaleLevel = ordinalIndex(HIJAB_LEVEL, femaleHijabLevel, "wears_hijab_level");

      if (requirement === null) {
        // No requirement (no preference / unknown / null) — full credit.
        totalPossiblePoints += hijabBasePoints * hijabImportance;
        earnedPoints += hijabBasePoints * hijabImportance;
        const noteLines = [
          `Female (${femaleName}) wears hijab: ${femaleHijabLevel || "Not specified"} (Male (${maleName}) seeking: ${malePreference || "No preference"}) ✓`,
          `  Male (${maleName})'s priority: ${maleLabel}`,
        ];
        details.hijab = {
          match: true,
          points: hijabBasePoints * hijabImportance,
          maxPoints: hijabBasePoints * hijabImportance,
          note: noteLines.join("\n"),
          importance: hijabImportanceDisplay,
        };
      } else if (femaleLevel === null) {
        // Unknown female hijab value — skip dimension entirely so the
        // score % still reflects only what we could score.
        details.hijab = {
          match: "not_considered",
          points: 0,
          note: `Could not score: unknown wears_hijab_level "${femaleHijabLevel ?? ""}"`,
          importance: hijabImportanceDisplay,
        };
      } else {
        totalPossiblePoints += hijabBasePoints * hijabImportance;
        let matchLevel: "perfect" | "close" | "none";
        if (femaleLevel >= requirement.preferred) matchLevel = "perfect";
        else if (femaleLevel >= requirement.min) matchLevel = "close";
        else matchLevel = "none";

        const emoji = matchLevel === "none" ? "✗" : "✓";
        const noteLines = [
          `Female (${femaleName}) wears hijab: ${femaleHijabLevel || "Not specified"} (Male (${maleName}) seeking: ${malePreference || "Not specified"}) ${emoji}`,
          `  Male (${maleName})'s priority: ${maleLabel}`,
        ];
        const formattedNote = noteLines.join("\n");

        if (matchLevel === "perfect") {
          earnedPoints += hijabBasePoints * hijabImportance;
          details.hijab = {
            match: true,
            points: hijabBasePoints * hijabImportance,
            maxPoints: hijabBasePoints * hijabImportance,
            note: formattedNote,
            importance: hijabImportanceDisplay,
          };
        } else if (matchLevel === "close") {
          const points = hijabBasePoints * 0.7 * hijabImportance;
          earnedPoints += points;
          details.hijab = {
            match: "close",
            points,
            maxPoints: hijabBasePoints * hijabImportance,
            note: formattedNote,
            importance: hijabImportanceDisplay,
          };
        } else {
          details.hijab = {
            match: false,
            points: 0,
            maxPoints: hijabBasePoints * hijabImportance,
            note: formattedNote,
            importance: hijabImportanceDisplay,
          };
        }
      }
    } else {
      details.hijab = { match: "not_considered", points: 0, note: "Hijab not a priority" };
    }
  } else {
    details.hijab = { match: "not_considered", points: 0, note: "Not applicable (no female profile)" };
  }

  // Map a female's beard preference to {min, preferred} ordinal indices into
  // BEARD_LEVEL. Returns null when there's no requirement (No preference /
  // unknown / null). The "Clean shaven" exact-match special case is handled
  // in the match logic itself, not here.
  const getBeardRequirement = (
    pref: string | null | undefined,
  ): { min: number; preferred: number } | null => {
    if (pref == null || isNoPreference(pref)) return null;
    switch (pref) {
      case "Full beard":
        return { min: BEARD_LEVEL.indexOf("Yes, trimmed"), preferred: BEARD_LEVEL.indexOf("Yes, Sunnah length") };
      case "Light beard":
        return { min: BEARD_LEVEL.indexOf("Goatee/mustache"), preferred: BEARD_LEVEL.indexOf("Yes, trimmed") };
      case "Clean shaven":
        return { min: BEARD_LEVEL.indexOf("Clean shaven"), preferred: BEARD_LEVEL.indexOf("Clean shaven") };
      default:
        console.warn(`[enums] Unknown beard preference "${pref}". Treating as no preference.`);
        return null;
    }
  };

  // BEARD COMPATIBILITY (female seeking male with beard)
  // Only the FEMALE's importance weight matters here
  if (profile1.gender === "male" || profile2.gender === "male") {
    const maleProfile = profile1.gender === "male" ? profile1 : profile2;
    const femaleProfile = profile1.gender === "female" ? profile1 : profile2;
    const femaleWeights = profile1.gender === "female" ? weights1 : weights2;
    const maleName = maleProfile.first_name || "Male";
    const femaleName = femaleProfile.first_name || "Female";

    const beardImportance = femaleWeights.beard ? IMPORTANCE_MULTIPLIERS[femaleWeights.beard as ImportanceLevel] : 0.0;
    const beardBasePoints = 15;

    // For display: only female's importance matters, male's is always "dont_care"
    const beardImportanceDisplay = {
      profile1: profile1.gender === "female" ? (femaleWeights.beard as ImportanceLevel || "dont_care") : "dont_care",
      profile2: profile2.gender === "female" ? (femaleWeights.beard as ImportanceLevel || "dont_care") : "dont_care",
    };

    if (beardImportance > 0) {
      const femalePreference = femaleProfile.seeking_beard_preference;
      const maleBeardStatus = maleProfile.has_beard;

      const femaleLabel = getImportanceLabel(femaleWeights.beard as ImportanceLevel);

      const requirement = getBeardRequirement(femalePreference);
      const maleLevel = ordinalIndex(BEARD_LEVEL, maleBeardStatus, "has_beard");

      if (requirement === null) {
        // No requirement — full credit.
        totalPossiblePoints += beardBasePoints * beardImportance;
        earnedPoints += beardBasePoints * beardImportance;
        const noteLines = [
          `Male (${maleName}) has beard: ${maleBeardStatus || "Not specified"} (Female (${femaleName}) seeking: ${femalePreference || "No preference"}) ✓`,
          `  Female (${femaleName})'s priority: ${femaleLabel}`,
        ];
        details.beard = {
          match: true,
          points: beardBasePoints * beardImportance,
          maxPoints: beardBasePoints * beardImportance,
          note: noteLines.join("\n"),
          importance: beardImportanceDisplay,
        };
      } else if (maleLevel === null) {
        // Unknown male beard value — skip dimension.
        details.beard = {
          match: "not_considered",
          points: 0,
          note: `Could not score: unknown has_beard "${maleBeardStatus ?? ""}"`,
          importance: beardImportanceDisplay,
        };
      } else {
        totalPossiblePoints += beardBasePoints * beardImportance;

        // Clean shaven is exact-match: only "Clean shaven" is perfect, only
        // "Goatee/mustache" is close. Anything more (trimmed/Sunnah) is a miss.
        let matchLevel: "perfect" | "close" | "none";
        if (femalePreference === "Clean shaven") {
          if (maleLevel === BEARD_LEVEL.indexOf("Clean shaven")) matchLevel = "perfect";
          else if (maleLevel === BEARD_LEVEL.indexOf("Goatee/mustache")) matchLevel = "close";
          else matchLevel = "none";
        } else if (maleLevel >= requirement.preferred) {
          matchLevel = "perfect";
        } else if (maleLevel >= requirement.min) {
          matchLevel = "close";
        } else {
          matchLevel = "none";
        }

        const emoji = matchLevel === "none" ? "✗" : "✓";
        const noteLines = [
          `Male (${maleName}) has beard: ${maleBeardStatus || "Not specified"} (Female (${femaleName}) seeking: ${femalePreference || "Not specified"}) ${emoji}`,
          `  Female (${femaleName})'s priority: ${femaleLabel}`,
        ];
        const formattedNote = noteLines.join("\n");

        if (matchLevel === "perfect") {
          earnedPoints += beardBasePoints * beardImportance;
          details.beard = {
            match: true,
            points: beardBasePoints * beardImportance,
            maxPoints: beardBasePoints * beardImportance,
            note: formattedNote,
            importance: beardImportanceDisplay,
          };
        } else if (matchLevel === "close") {
          const points = beardBasePoints * 0.7 * beardImportance;
          earnedPoints += points;
          details.beard = {
            match: "close",
            points,
            maxPoints: beardBasePoints * beardImportance,
            note: formattedNote,
            importance: beardImportanceDisplay,
          };
        } else {
          details.beard = {
            match: false,
            points: 0,
            maxPoints: beardBasePoints * beardImportance,
            note: formattedNote,
            importance: beardImportanceDisplay,
          };
        }
      }
    } else {
      details.beard = { match: "not_considered", points: 0, note: "Beard not a priority" };
    }
  } else {
    details.beard = { match: "not_considered", points: 0, note: "Not applicable (no male profile)" };
  }

  // MASJID ATTENDANCE COMPATIBILITY
  const masjidImportance1 = IMPORTANCE_MULTIPLIERS[weights1.masjid_attendance as ImportanceLevel] || 1.5;
  const masjidImportance2 = IMPORTANCE_MULTIPLIERS[weights2.masjid_attendance as ImportanceLevel] || 1.5;
  const masjidBasePoints = 10;
  const masjidWeight = (masjidImportance1 + masjidImportance2) / 2;

  if (masjidWeight > 0) {
    const minMasjid1Raw = profile1.seeking_masjid_attendance_min;
    const minMasjid2Raw = profile2.seeking_masjid_attendance_min;

    // No-preference handling: only the literal "No preference" sentinel or
    // null/empty means "no preference". "Never" is a REAL attendance level —
    // not a no-preference alias.
    const noPref1 = !minMasjid1Raw || isNoPreference(minMasjid1Raw);
    const noPref2 = !minMasjid2Raw || isNoPreference(minMasjid2Raw);

    const minIndex1 = noPref1
      ? null
      : ordinalIndex(MASJID_ATTENDANCE, minMasjid1Raw, "seeking_masjid_attendance_min");
    const minIndex2 = noPref2
      ? null
      : ordinalIndex(MASJID_ATTENDANCE, minMasjid2Raw, "seeking_masjid_attendance_min");
    const level1Index = ordinalIndex(MASJID_ATTENDANCE, profile1.masjid_attendance, "masjid_attendance");
    const level2Index = ordinalIndex(MASJID_ATTENDANCE, profile2.masjid_attendance, "masjid_attendance");

    // If either side has a real (non-no-pref) requirement but the relevant
    // value can't be resolved on the SSoT list, skip the dimension entirely.
    const side1Unresolvable = !noPref1 && (minIndex1 === null || level2Index === null);
    const side2Unresolvable = !noPref2 && (minIndex2 === null || level1Index === null);

    if (side1Unresolvable || side2Unresolvable) {
      details.masjidAttendance = {
        match: "not_considered",
        points: 0,
        note: "Could not score: unknown masjid_attendance value",
        importance: {
          profile1: weights1.masjid_attendance as ImportanceLevel,
          profile2: weights2.masjid_attendance as ImportanceLevel,
        },
      };
    } else {
      totalPossiblePoints += masjidBasePoints * masjidWeight;

      const meetsMin1 = noPref1 || (level2Index! >= minIndex1!);
      const meetsMin2 = noPref2 || (level1Index! >= minIndex2!);

      const p1Label = getImportanceLabel(weights1.masjid_attendance as ImportanceLevel);
      const p2Label = getImportanceLabel(weights2.masjid_attendance as ImportanceLevel);
      const emoji1 = meetsMin2 ? "✓" : "✗";
      const emoji2 = meetsMin1 ? "✓" : "✗";

      const minMasjid1Display = minMasjid1Raw || "No preference";
      const minMasjid2Display = minMasjid2Raw || "No preference";
      const noteLines = [
        `${name1} attends masjid ${profile1.masjid_attendance} (${name2} seeking: min ${minMasjid2Display}) ${emoji1}`,
        `  ${name2}'s priority: ${p2Label}`,
        `${name2} attends masjid ${profile2.masjid_attendance} (${name1} seeking: min ${minMasjid1Display}) ${emoji2}`,
        `  ${name1}'s priority: ${p1Label}`,
      ];
      const formattedNote = noteLines.join("\n");

      if (meetsMin1 && meetsMin2) {
        earnedPoints += masjidBasePoints * masjidWeight;
        details.masjidAttendance = {
          match: true,
          points: masjidBasePoints * masjidWeight,
          maxPoints: masjidBasePoints * masjidWeight,
          note: formattedNote,
          importance: {
            profile1: weights1.masjid_attendance as ImportanceLevel,
            profile2: weights2.masjid_attendance as ImportanceLevel,
          },
        };
      } else {
        details.masjidAttendance = {
          match: false,
          points: 0,
          maxPoints: masjidBasePoints * masjidWeight,
          note: formattedNote,
          importance: {
            profile1: weights1.masjid_attendance as ImportanceLevel,
            profile2: weights2.masjid_attendance as ImportanceLevel,
          },
        };
      }
    }
  } else {
    details.masjidAttendance = { match: "not_considered", points: 0, note: "Masjid attendance not a priority for both" };
  }

  // MARITAL STATUS (respecting importance weights)
  const maritalImportance1 = IMPORTANCE_MULTIPLIERS[weights1.marital_status as ImportanceLevel] || 1.0;
  const maritalImportance2 = IMPORTANCE_MULTIPLIERS[weights2.marital_status as ImportanceLevel] || 1.0;

  const maritalOptions1 = profile1.seeking_marital_status || ["Never Married", "Divorced", "Widowed"];
  const maritalOptions2 = profile2.seeking_marital_status || ["Never Married", "Divorced", "Widowed"];

  const maritalMatch1 = maritalOptions2.includes(profile1.marital_status);
  const maritalMatch2 = maritalOptions1.includes(profile2.marital_status);

  const maritalP1Label = getImportanceLabel(weights1.marital_status as ImportanceLevel);
  const maritalP2Label = getImportanceLabel(weights2.marital_status as ImportanceLevel);
  const maritalEmoji1 = maritalMatch2 ? "✓" : "✗";
  const maritalEmoji2 = maritalMatch1 ? "✓" : "✗";

  const maritalNoteLines = [
    `${name1} is ${profile1.marital_status} (${name2} seeking: ${maritalOptions2.join(", ")}) ${maritalEmoji1}`,
    `  ${name2}'s priority: ${maritalP2Label}`,
    `${name2} is ${profile2.marital_status} (${name1} seeking: ${maritalOptions1.join(", ")}) ${maritalEmoji2}`,
    `  ${name1}'s priority: ${maritalP1Label}`
  ];
  const formattedMaritalNote = maritalNoteLines.join('\n');

  if (!maritalMatch1 || !maritalMatch2) {
    // Check if it's a deal-breaker
    if (weights1.marital_status === "deal_breaker" || weights2.marital_status === "deal_breaker") {
      dealBreakerFailed = true;
      dealBreakers.push(`Marital status mismatch: ${formattedMaritalNote}`);
    } else {
      dealBreakers.push(`Marital status mismatch`);
    }
    details.maritalStatus = {
      match: false,
      points: 0,
      note: formattedMaritalNote,
      importance: { profile1: weights1.marital_status as ImportanceLevel, profile2: weights2.marital_status as ImportanceLevel },
    };
  } else {
    details.maritalStatus = {
      match: true,
      points: 0,
      note: formattedMaritalNote,
      importance: { profile1: weights1.marital_status as ImportanceLevel, profile2: weights2.marital_status as ImportanceLevel },
    };
  }

  // CHILDREN (yes/no only, no importance weights)
  const childrenOk1 = profile2.seeking_children_ok ?? true;
  const childrenOk2 = profile1.seeking_children_ok ?? true;

  const childrenMatch1 = !profile1.has_children || childrenOk2;
  const childrenMatch2 = !profile2.has_children || childrenOk1;
  const childrenEmoji1 = childrenMatch1 ? "✓" : "✗";
  const childrenEmoji2 = childrenMatch2 ? "✓" : "✗";

  const childrenNoteLines = [
    `${name1} has children: ${profile1.has_children ? "Yes" : "No"} (${name2} OK with children: ${childrenOk2 ? "Yes" : "No"}) ${childrenEmoji1}`,
    `${name2} has children: ${profile2.has_children ? "Yes" : "No"} (${name1} OK with children: ${childrenOk1 ? "Yes" : "No"}) ${childrenEmoji2}`
  ];
  const formattedChildrenNote = childrenNoteLines.join('\n');

  if (!childrenMatch1 || !childrenMatch2) {
    details.children = {
      match: false,
      points: 0,
      note: formattedChildrenNote,
    };
  } else {
    details.children = {
      match: true,
      points: 0,
      note: formattedChildrenNote,
    };
  }

  // Calculate final score
  const finalScore = totalPossiblePoints > 0 ? Math.round((earnedPoints / totalPossiblePoints) * 100) : 0;

  // Determine match type
  let matchType: "perfect" | "close" | "soft" | "low";
  if (finalScore >= 90 && dealBreakers.length === 0) {
    matchType = "perfect";
  } else if (finalScore >= 80 && dealBreakers.length === 0) {
    matchType = "close";
  } else if (finalScore >= 70 && dealBreakers.length === 0) {
    matchType = "soft";
  } else {
    matchType = "low";
  }

  // Calculate weighted factors
  const mostImportantToProfile1 = Object.entries(weights1)
    .filter(([, v]) => v === "very_important" || v === "deal_breaker")
    .map(([k]) => k);

  const mostImportantToProfile2 = Object.entries(weights2)
    .filter(([, v]) => v === "very_important" || v === "deal_breaker")
    .map(([k]) => k);

  const mutualPriorities = mostImportantToProfile1.filter((item) => mostImportantToProfile2.includes(item));

  return {
    score: finalScore,
    totalPossiblePoints,
    earnedPoints,
    details,
    dealBreakerFailed,
    dealBreakers,
    matchType,
    weightedFactors: {
      mostImportantToProfile1,
      mostImportantToProfile2,
      mutualPriorities,
    },
  };
}

export function getImportanceLabel(level: ImportanceLevel): string {
  const labels: Record<ImportanceLevel, string> = {
    deal_breaker: "🔴 Deal-Breaker",
    very_important: "🟠 Very Important",
    important: "🟡 Important",
    somewhat_important: "🟢 Somewhat Important",
    flexible: "🔵 Flexible",
    dont_care: "⚪ Don't Care",
  };
  return labels[level] || level;
}

export function getImportanceColor(level: ImportanceLevel): string {
  const colors: Record<ImportanceLevel, string> = {
    deal_breaker: "text-red-600 dark:text-red-400",
    very_important: "text-orange-600 dark:text-orange-400",
    important: "text-yellow-600 dark:text-yellow-400",
    somewhat_important: "text-green-600 dark:text-green-400",
    flexible: "text-blue-600 dark:text-blue-400",
    dont_care: "text-muted-foreground",
  };
  return colors[level] || "";
}
